import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { probeMetaConversionsApi, trackPageView } from '../services/metaTracking';
import { isAuthActionUrlActive } from '../utils/authActionQuery.mjs';

const META_PIXEL_ID = String(import.meta.env.VITE_META_PIXEL_ID || '1475394377573862').trim();
const META_PIXEL_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const META_LOCATION_CHANGE_EVENT = 'meta-location-change';
let lastTrackedPath = '';
let metaPixelBootPromise = null;

const getCurrentPathWithQuery = () => {
  if (typeof window === 'undefined') return '';
  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';
  return `${pathname}${search}`;
};

const dispatchLocationChange = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(META_LOCATION_CHANGE_EVENT));
};

const ensureMetaPixelScript = async () => {
  if (typeof window === 'undefined' || !META_PIXEL_ID) return false;
  if (typeof window.fbq === 'function' && window.__stocktagMetaPixelInitialized) return true;
  if (metaPixelBootPromise) return metaPixelBootPromise;

  metaPixelBootPromise = new Promise((resolve) => {
    if (typeof window.fbq !== 'function') {
      const fbq = function fbqProxy(...args) {
        if (typeof fbq.callMethod === 'function') {
          fbq.callMethod.apply(fbq, args);
          return;
        }
        fbq.queue.push(args);
      };

      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = '2.0';
      window.fbq = fbq;
      window._fbq = fbq;
    }

    const existingScript = document.querySelector(`script[data-stocktag-meta-pixel="true"]`);
    if (existingScript) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = META_PIXEL_SCRIPT_SRC;
    script.dataset.stocktagMetaPixel = 'true';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  }).finally(() => {
    metaPixelBootPromise = null;
  });

  return metaPixelBootPromise;
};

const grantMetaConsent = () => {
  if (typeof window.fbq !== 'function') return;
  try {
    window.fbq('consent', 'grant');
  } catch {
    // noop
  }
};

const revokeMetaConsent = () => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq('consent', 'revoke');
  } catch {
    // noop
  }
};

const initializeMetaPixel = () => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function' || !META_PIXEL_ID) return false;
  if (!window.__stocktagMetaPixelInitialized) {
    window.fbq('init', META_PIXEL_ID);
    window.__stocktagMetaPixelInitialized = true;
  }
  grantMetaConsent();
  return true;
};

const MetaPixelTracker = ({ consent }) => {
  useEffect(() => {
    if (!consent || typeof window === 'undefined' || !META_PIXEL_ID) return undefined;
    // The pixel attaches the document URL to every event, so it must not even
    // load while a Firebase auth-action oobCode is in the query string.
    if (isAuthActionUrlActive()) return undefined;

    let isCancelled = false;
    let originalPushState = null;
    let originalReplaceState = null;

    const emitPageView = () => {
      const currentPath = getCurrentPathWithQuery();
      if (!currentPath || currentPath === lastTrackedPath) return;
      lastTrackedPath = currentPath;
      trackPageView({ path: currentPath });
    };

    const attachRouteTracking = () => {
      originalPushState = window.history.pushState;
      originalReplaceState = window.history.replaceState;

      window.history.pushState = function patchedPushState(...args) {
        const result = originalPushState.apply(this, args);
        dispatchLocationChange();
        return result;
      };

      window.history.replaceState = function patchedReplaceState(...args) {
        const result = originalReplaceState.apply(this, args);
        dispatchLocationChange();
        return result;
      };

      emitPageView();
      window.addEventListener('popstate', emitPageView);
      window.addEventListener('hashchange', emitPageView);
      window.addEventListener(META_LOCATION_CHANGE_EVENT, emitPageView);
    };

    void (async () => {
      const loaded = await ensureMetaPixelScript();
      if (isCancelled || !loaded) return;
      if (!initializeMetaPixel()) return;
      void probeMetaConversionsApi();
      attachRouteTracking();
    })();

    return () => {
      isCancelled = true;
      if (originalPushState) {
        window.history.pushState = originalPushState;
      }
      if (originalReplaceState) {
        window.history.replaceState = originalReplaceState;
      }
      window.removeEventListener('popstate', emitPageView);
      window.removeEventListener('hashchange', emitPageView);
      window.removeEventListener(META_LOCATION_CHANGE_EVENT, emitPageView);
    };
  }, [consent]);

  useEffect(() => {
    if (consent) return;
    revokeMetaConsent();
  }, [consent]);

  return null;
};

MetaPixelTracker.propTypes = {
  consent: PropTypes.bool.isRequired
};

export default MetaPixelTracker;
